import {
  InputWrapper,
  type InputWrapperProps,
} from '@veraclins-dev/ui/input-wrapper';

import { type BaseInputProps, useFieldProperties } from '../field-helpers';

export interface InputFieldWrapperProps
  extends React.ComponentProps<'div'>, BaseInputProps {
  children: React.ReactNode;
  plain?: boolean;
  /**
   * Ref for the styled input container — the element `InputWrapper` applies
   * `inputContainerVariants` to. Anchor a popover to the field's own box with
   * it instead of rebuilding that box inside the wrapper.
   */
  containerRef?: React.Ref<HTMLDivElement>;
  // The input-variant style-prop shape `InputWrapper` forwards to its container.
  // Taken from `InputWrapper`'s own prop type so form never reaches for the
  // `@veraclins-dev/ui/variants` index (which would re-pull every variant module).
  inputProps?: InputWrapperProps['inputProps'];
  /**
   * Id for the rendered label. Standalone fields mint their own id, so a
   * control that names itself from the label passes the id it points at.
   */
  labelId?: InputWrapperProps['labelId'];
}

/**
 * Wraps a control with its label and validation errors, and wires up the
 * accessible ids.
 *
 * A field-authoring primitive, like `useFieldProperties` and `getInputProps`
 * beside it. This package's own fields compose it (`TextField`, `EditorField`
 * and others). It is still exported, with its own subpath, but it is not
 * something to drop straight into a page.
 * `@internal` keeps it out of the docs — no page, no nav entry.
 *
 * @internal
 */
export const InputFieldWrapper = ({
  field,
  ...props
}: InputFieldWrapperProps) => {
  const { errorId, id, errors, labelId } = useFieldProperties(field);

  return (
    <InputWrapper
      id={id}
      labelId={labelId}
      errors={errors}
      errorId={errorId}
      {...props}
    />
  );
};
